// GENERAL

// Dialog chrome data-test-ids (MDialog / MDialogShell — shared by every dialog)
export const dialogIds = {
  dialogWindow: 'dialog-window',
  closeButton: 'dialog-close-button',
  scoutButton: 'dialog-scout-button',
} as const;

// Default Drawer data-test-ids
export const defaultDrawerIds: { [key: string]: string } = {
  drawerProjectsIcon: 'drawer-project-icon',
  drawerDashboardIcon: 'drawer-dashboard-icon',
  drawerOrganizationIcon: 'drawer-organization-icon',
  drawerVendorsIcon: 'drawer-vendors-icon',
  drawerPoliciesIcon: 'drawer-policies-icon',
  drawerSupportIcon: 'drawer-support-icon',
  drawerOpenIcon: 'drawer-open-icon',
  drawerCloseIcon: 'drawer-close-icon',
  drawerUserProfile: 'drawer-user-profile',
};

// Header data-test-ids
export const headerIds: { [key: string]: string } = {
  notificationsIcon: 'notifications-icon',
  notificationsUnreadIcon: 'notifications-unread-icon',
  greetingsText: 'greetings-text',
  scoutButton: 'header-scout-button',
};

// Project Creation data-test-ids
export const projectCreationIds: { [key: string]: string } = {
  projectName: 'project-name-input',
  projectGeographicScope: 'project-geographic-scope',
  projectIntendedUse: 'project-intended-use',
  projectCurrentState: 'project-current-state',
  projectTargetGoLiveDate: 'project-target-go-live-date',
  projectCreationAddUserButton: 'project-creation-add-user-button',
  projectCreationResetFrameworksButton: 'project-creation-reset-Frameworks-button',
};

// AI System name-only create dialog data-test-ids
export const createAiSystemIds = {
  modal: 'create-ai-system-modal',
  nameInput: 'create-ai-system-name-input',
  ownerSelect: 'create-ai-system-owner-select',
  createButton: 'create-ai-system-create-button',
} as const;

// Mixed data-test-ids
export const mixedIds: { [key: string]: string } = {
  arrowBackButton: 'arrow-back-button',
  pageTitle: 'page-title',
  editTitleButton: 'edit-title-button',
  editDescriptionButton: 'edit-description-button',
};

// Organization

export const organizationIds: { [key: string]: string } = {
  organizationTitleText: 'page-title',
  organizationRiskManagement: 'organization-risk-management',
  organizationUserManagement: 'organization-user-management',
  organizationFrameworks: 'organization-frameworks',
  organizationSettings: 'organization-settings',
  organizationUsage: 'organization-usage',
};

// Users Management

export const organizationUserManagementIds: { [key: string]: string } = {
  usersManagementAddUserButton: 'users-management-add-user-button',
  usersManagementUsersTab: 'users-management-users-tab',
  usersManagementRolesTab: 'users-management-roles-tab',
  usersManagementUsersTable: 'users-management-users-table',
  usersManagementRolesTable: 'users-management-roles-table',
};

// Project general

// Settings data-test-ids
export const projectSettingsIds: { [key: string]: string } = {
  // Users Tab
  projectSettingsUsersTab: 'project-settings-users-tab',
  addProjectUserModalDropdown: 'add-project-user-modal-dropdown',
  // EU AI Act Tab
  projectSettingsEuActButton: 'project-settings-eu-act-button',
  projectSettingsEuActTab: 'project-settings-eu-act-tab',
  euActTabForm: 'eu-act-tab-form',
  euActTabSaveButton: 'eu-act-tab-save-button',
  euActTabProductCategorySelect: 'eu-act-tab-product-category-select',
  euActTabRoleSelect: 'eu-act-tab-role-select',
  euActTabUseCaseSelect: 'eu-act-tab-use-case-select',
  euActTabEstablishmentSelect: 'eu-act-tab-establishment-select',
  euActTabModelPropertySelect: 'eu-act-tab-model-property-select',
  euActTabProjectRequirementsSelect: 'eu-act-tab-project-requirements-select',
  euActTabCriticalIssuesSelect: 'eu-act-tab-critical-issues-select',
  euActTabSupplyTermsSelect: 'eu-act-tab-supply-terms-select',
  euActTabScopePreviewDialog: 'eu-act-tab-scope-preview-dialog',
  euActTabScopePreviewDialogApplyButton: 'eu-act-tab-scope-preview-dialog-apply-button',
  // Sources
  projectSettingsSourcesTab: 'project-settings-sources-tab',
  // Frameworks Tab
  projectSettingsAddFrameworkButton: 'project-settings-add-framework-button',
};

// CONTROL

// Control data-test-ids
export const controlIds: { [key: string]: string } = {
  controlStatusSelect: 'control-status-select',
  controlOwnerSelect: 'control-owner-select',
  controlCodeText: 'control-code-text',
  controlReportAiBadge: 'control-report-ai-badge',
  controlReportEditor: 'control-report-editor',
  controlReportAiEditor: 'control-report-ai-editor',
  controlReportAiEditorEditIcon: 'control-report-ai-editor-edit-icon',
  controlReportAiEditorDeleteIcon: 'control-report-ai-editor-delete-icon',
  controlReportAiEditorRefreshIcon: 'control-report-ai-editor-refresh-icon',
  controlAssessmentExcludedEvidence: 'control-assessment-excluded-evidence',
};

// Requirement data-test-ids
export const requirementIds = {
  requirementOwnerSelect: 'requirement-owner-select',
} as const;

// Asset data-test-ids
export const assetIds = {
  assetOwnerSelect: 'asset-owner-select',
} as const;

// Review request data-test-ids
export const reviewRequestIds = {
  reviewerMultiSelect: 'reviewer-multi-select',
} as const;

// Control Evidence Component data-test-ids
export const controlEvidenceComponentIds: { [key: string]: string } = {
  controlEvidenceComponentPreviewEvidenceIcon: 'control-evidence-component-preview-evidence-icon',
  controlEvidenceComponentDeleteEvidenceIcon: 'control-evidence-component-delete-evidence-icon',
};

// Control Policy Evidence data-test-ids
export const controlPolicyEvidenceIds: { [key: string]: string } = {
  linkPolicyButton: 'control-policy-evidence-link-policy-button',
  policyListItem: 'control-policy-evidence-policy-list-item',
  removePolicyButton: 'control-policy-evidence-remove-policy-button',
};

// RISK

// Risk creation
export const riskCreation: { [key: string]: string } = {
  riskCreationCategory: 'risk-category',
  riskType: 'risk-type',
};

// Risk quantification
export const riskQuantify: { [key: string]: string } = {
  riskManualEntryValue: 'risk-manual-entry-value',
  riskManualDescription: 'risk-manual-description',
  credibleLow: 'credible-low',
  credibleHigh: 'credible-high',
};

export const quantificationMethodIds: { [key: string]: string } = {
  riskAgent: 'quantification-method-risk_agent',
  matrix: 'quantification-method-matrix',
  manual: 'quantification-method-manual',
  scenarioAnalysis: 'quantification-method-scenario_analysis',
  monteCarlo: 'quantification-method-monte_carlo',
};

// TESTING

export const testingFormIds: { [key: string]: string } = {
  operatorSelector: 'operator-select',
  sourceSelector: 'source-select',
  enabledToggle: 'toggle-enable',
  scheduleFrequency: 'schedule-frequency-selector',
  scheduleTime: 'schedule-time-selector',
};

// VENDORS

export const vendorsOverviewIds: { [key: string]: string } = {
  vendorsOverviewTitleText: 'vendors-overview-title-text',
  vendorsOverviewNewVendorButton: 'vendors-overview-new-vendor-button',
  vendorsOverviewTypeSelect: 'vendors-overview-type-select',
  vendorsOverviewStatusSelect: 'vendors-overview-status-select',
  vendorsOverviewRiskLevelSelect: 'vendors-overview-risk-level-select',
};

export const createVendorIds: { [key: string]: string } = {
  createVendorNameInput: 'create-vendor-name-input',
  createVendorTypeSelect: 'create-vendor-type-select',
  createVendorWebsiteInput: 'create-vendor-website-input',
  createVendorDescriptionInput: 'create-vendor-description-input',
  createVendorAdressStreetInput: 'create-vendor-adress-street-input',
  createVendorAdressNumberInput: 'create-vendor-adress-number-input',
  createVendorAdressZipCodeInput: 'create-vendor-adress-zip-code-input',
  createVendorAdressCityInput: 'create-vendor-adress-city-input',
  createVendorAdressStateInput: 'create-vendor-adress-state-input',
  createVendorAdressCountrySelect: 'create-vendor-adress-country-select',
  createVendorResponsiblePersonSelect: 'create-vendor-responsible-person-select',
  createVendorResponsiblePersonMenu: 'create-vendor-responsible-person-menu',
  createVendorStatusSelect: 'create-vendor-status-select',
  createVendorRiskLevelSelect: 'create-vendor-risk-level-select',
  createVendorReviewDateInput: 'create-vendor-review-date-input',
  createVendorAnnualContractInput: 'create-vendor-annual-contract-input',
  createVendorSubprocessorCheckbox: 'create-vendor-subprocessor-checkbox',
  createVendorPrivacyPolicyInput: 'create-vendor-privacy-policy-input',
  createVendorTermsOfServiceInput: 'create-vendor-terms-of-service-input',
  createVendorAddButton: 'create-vendor-add-button',
};

export const editVendorIds: { [key: string]: string } = {
  editVendorNameInput: 'edit-vendor-name-input',
  editVendorTypeSelect: 'edit-vendor-type-select',
  editVendorWebsiteInput: 'edit-vendor-website-input',
  editVendorDescriptionInput: 'edit-vendor-description-input',
  editVendorAdressStreetInput: 'edit-vendor-adress-street-input',
  editVendorAdressNumberInput: 'edit-vendor-adress-number-input',
  editVendorAdressZipCodeInput: 'edit-vendor-adress-zip-code-input',
  editVendorAdressCityInput: 'edit-vendor-adress-city-input',
  editVendorAdressStateInput: 'edit-vendor-adress-state-input',
  editVendorAdressCountrySelect: 'edit-vendor-adress-country-select',
  editVendorResponsiblePersonSelect: 'edit-vendor-responsible-person-select',
  editVendorResponsiblePersonMenu: 'edit-vendor-responsible-person-menu',
  editVendorStatusSelect: 'edit-vendor-status-select',
  editVendorRiskLevelSelect: 'edit-vendor-risk-level-select',
  editVendorReviewDateInput: 'edit-vendor-review-date-input',
  editVendorAnnualContractInput: 'edit-vendor-annual-contract-input',
  editVendorSubprocessorCheckbox: 'edit-vendor-subprocessor-checkbox',
  editVendorPrivacyPolicyInput: 'edit-vendor-privacy-policy-input',
  editVendorTermsOfServiceInput: 'edit-vendor-terms-of-service-input',
  editVendorSaveButton: 'edit-vendor-save-button',
};

export const vendorDetailsIds: { [key: string]: string } = {
  vendorDetailsName: 'vendor-details-name',
  vendorDetailsWebsite: 'vendor-details-website',
  vendorDetailsDescription: 'vendor-details-description',
  vendorDetailsAdress: 'vendor-details-adress',
  vendorDetailsPrivacyPolicy: 'vendor-details-privacy-policy',
  vendorDetailsTermsOfService: 'vendor-details-terms-of-service',
  vendorDetailsResponsiblePerson: 'vendor-details-responsible-person',
  vendorDetailsStatus: 'vendor-details-status',
  vendorDetailsRiskLevel: 'vendor-details-risk-level',
  vendorDetailsReviewDate: 'vendor-details-review-date',
  vendorDetailsAnnualContract: 'vendor-details-annual-contract',
  vendorDetailsSubprocessor: 'vendor-details-subprocessor',
  vendorDetailsType: 'vendor-details-type',
  vendorDetailsDeleteButton: 'vendor-details-delete-button',
  vendorDocsActionsMenu: 'vendor-documents-actions-menu',
};

export const vendorDocsActionsMenuIds = {
  downloadMenuItem: 'vendor-docs-download-menu-item',
  deleteMenuItem: 'vendor-docs-delete-menu-item',
} as const;

// MODALS

export const profileModalIds: { [key: string]: string } = {
  profileModalLogoutIcon: 'profile-modal-logout-icon',
  profileModalSettingsIcon: 'profile-modal-settings-icon',
  profileModalTokenIcon: 'profile-modal-token-icon',
};

export const userSettingsIds: { [key: string]: string } = {
  userSettingsStartTotpButton: 'user-settings-start-totp-button',
  userSettingsTotpSecretInput: 'user-settings-totp-secret-input',
  userSettingsCopyTotpSecretButton: 'user-settings-copy-totp-secret-button',
  userSettingsTotpUriInput: 'user-settings-totp-uri-input',
  userSettingsCopyTotpUriButton: 'user-settings-copy-totp-uri-button',
  userSettingsTotpCodeInput: 'user-settings-totp-code-input',
  userSettingsVerifyTotpButton: 'user-settings-verify-totp-button',
  userSettingsRemoveTotpButton: 'user-settings-remove-totp-button',
};

export const evidencePreviewIds: { [key: string]: string } = {
  evidencePreviewCreatedByValue: 'evidence-preview-created-by-value',
  evidencePreviewTypeValue: 'evidence-preview-type-value',
  evidencePreviewLastUpdateValue: 'evidence-preview-last-update-value',
  evidencePreviewFileSizeValue: 'evidence-preview-file-size-value',
};

// New Evidence Modal data-test-ids
export const newEvidenceIds: { [key: string]: string } = {
  newEvidenceNameValue: 'new-evidence-name-value',
  newEvidenceDescriptionValue: 'new-evidence-description-value',
  newEvidenceAiBadge: 'new-evidence-ai-badge',
  newEvidenceAiEditor: 'new-evidence-ai-editor',
  newEvidenceEditNameIcon: 'new-evidence-edit-name',
  newEvidenceDeleteNameIcon: 'new-evidence-delete-name',
  newEvidenceRefreshNameIcon: 'new-evidence-delete-name',
  newEvidenceEditDescriptionIcon: 'new-evidence-edit-description',
  newEvidenceDeleteDescriptionIcon: 'new-evidence-delete-description',
  newEvidenceRefreshDescriptionIcon: 'new-evidence-delete-name',
  controlSelect: 'evidence-control-select',
};

// Edit Evidence Modal data-test-ids
export const editEvidenceIds: { [key: string]: string } = {
  controlSelect: 'evidence-control-select',
  editEvidenceEditNameIcon: 'edit-evidence-edit-name',
  editEvidenceDeleteNameIcon: 'edit-evidence-delete-name',
  editEvidenceRefreshNameIcon: 'edit-evidence-delete-name',
  editEvidenceEditDescriptionIcon: 'edit-evidence-edit-description',
  editEvidenceDeleteDescriptionIcon: 'edit-evidence-delete-description',
  editEvidenceRefreshDescriptionIcon: 'edit-evidence-delete-name',
};

export const confirmationModalIds: { [key: string]: string } = {
  confirmationModal: 'confirmation-modal',
  confirmationModalTitle: 'confirmation-modal-title',
  confirmationModalConfirmButton: 'confirmation-modal-confirm-button',
  confirmationModalCancelButton: 'confirmation-modal-cancel-button',
};

export const tokenModalIds: { [key: string]: string } = {
  tokenModalCopyIcon: 'token-modal-copy-icon',
};

export const invitedUserModalIds: { [key: string]: string } = {
  resendInvitationButton: 'resend-invitation-button',
  cancelInvitationButton: 'cancel-invitation-button',
};

export const organizationDialogIds = {
  dialog: 'organization-dialog',
};

export const editUserModalIds: { [key: string]: string } = {
  editUserModal: 'edit-user-modal',
  editUserModalActivateButton: 'edit-user-modal-activate-button',
  editUserModalDeactivateButton: 'edit-user-modal-deactivate-button',
  editUserModalResetPasswordButton: 'edit-user-modal-reset-password-button',
  editUserModalUnlockButton: 'edit-user-modal-unlock-button',
  editUserAdminCheckbox: 'admin-role-checkbox',
  editUserRiskManagerCheckbox: 'risk-manager-role-checkbox',
  editUserPolicyManagerCheckbox: 'policy-manager-role-checkbox',
  editUserRegistrationManagerCheckbox: 'registration-manager-role-checkbox',
};

// TABLE
export const tableIds: { [key: string]: string } = {
  tableComponent: 'table-component',
  tableSearchInput: 'table-search-input',
  tableEvidenceActions: 'table-evidence-actions-button',
};

export const evidenceActionsMenuIds = {
  copyLinkMenuItem: 'evidence-copy-link-menu-item',
  previewMenuItem: 'evidence-preview-menu-item',
  downloadMenuItem: 'evidence-download-menu-item',
  deleteMenuItem: 'evidence-delete-menu-item',
} as const;

// Modulos Assistant Chat
export const chatIds: { [key: string]: string } = {
  chatDialog: 'chat-dialog',
  chatHeader: 'chat-header',
  chatMessage: 'chat-message',
  chatCloseButton: 'chat-close-button',
  chatExpandButton: 'chat-expand-button',
  chatQueryInput: 'chat-query-input',
  chatSourceUrl: 'chat-source-url',
  chatSourceToken: 'chat-source-token',
  chatSourceBranch: 'chat-source-branch',
  chatAddSourceButton: 'chat-add-source-button',
  chatRemoveSourceButton: 'chat-remove-source-button',
  chatStartButton: 'chat-start-button',
  chatCancelButton: 'chat-cancel-button',
  chatThought: 'chat-thought',
  chatError: 'chat-error',
};

// POLICIES

export const policySidebarIds = {
  sidebarMyTasks: 'sidebar-my-tasks',
  sidebarPolicyManagement: 'sidebar-policy-management',
  sidebarTemplatesCatalog: 'sidebar-templates-catalog',
  sidebarAcknowledgements: 'sidebar-acknowledgements',
} as const;

export const policyVersionDetailIds = {
  pageTitle: 'policy-version-page-title',
  askApprovalButton: 'policy-version-ask-approval-button',
  approveButton: 'policy-version-approve-button',
  rejectButton: 'policy-version-reject-button',
  publishButton: 'policy-version-publish-button',
  moveToDraftButton: 'policy-version-move-to-draft-button',
  unarchiveButton: 'policy-version-unarchive-button',
  moreMenu: 'policy-version-more-menu',
  archiveMenuItem: 'policy-version-archive-menu-item',
  exportMenuItem: 'policy-version-export-menu-item',
} as const;

export const publishPolicyIds = {
  modal: 'publish-policy-modal',
  renewalPeriod: 'publish-renewal-period',
  confirmButton: 'publish-policy-confirm-button',
  cancelButton: 'publish-policy-cancel-button',
} as const;

export const policyDetailIds = {
  openDraftButton: 'policy-detail-open-draft-button',
  unarchiveButton: 'policy-detail-unarchive-button',
  moreMenu: 'policy-detail-more-menu',
  archiveMenuItem: 'policy-detail-archive-menu-item',
  exportMenuItem: 'policy-detail-export-menu-item',
} as const;

export const policyVersionInfoIds = {
  statusBadge: 'policy-version-status-badge',
  owner: 'policy-version-owner',
  createdAt: 'policy-version-created-at',
  approvedAt: 'policy-version-approved-at',
  publishedAt: 'policy-version-published-at',
  sourceTemplate: 'policy-version-source-template',
} as const;

export const policyInfoIds = {
  statusBadge: 'policy-info-status-badge',
  owner: 'policy-info-owner',
  version: 'policy-info-version',
  createdAt: 'policy-info-created-at',
  approvedAt: 'policy-info-approved-at',
  publishedAt: 'policy-info-published-at',
  renewalAt: 'policy-info-renewal-at',
  renewalPeriod: 'policy-info-renewal-period',
  sourceTemplate: 'policy-info-source-template',
} as const;

export const policyPanelIds = {
  nameInput: 'policy-name-input',
  contentEditor: 'policy-content-editor',
  saveButton: 'policy-save-button',
  pdfCard: 'policy-pdf-card',
  pdfDropzone: 'policy-pdf-dropzone',
  pdfReplace: 'policy-pdf-replace',
  pdfRemove: 'policy-pdf-remove',
  pdfDownload: 'policy-pdf-download',
  contentToggleEditor: 'policy-content-toggle-editor',
  contentToggleUpload: 'policy-content-toggle-upload',
} as const;

export const myPolicyDetailIds = {
  acknowledgeCheckbox: 'policy-acknowledge-checkbox',
  acknowledgeButton: 'policy-acknowledge-button',
  cancelButton: 'policy-acknowledge-cancel-button',
  acknowledgedStatus: 'policy-acknowledged-status',
  owner: 'my-policy-detail-owner',
  version: 'my-policy-detail-version',
  published: 'my-policy-detail-published',
} as const;

export const myPoliciesPageIds = {
  search: 'my-policies-search',
  statusFilter: 'my-policies-status-filter',
  list: 'my-policies-list',
  policyCard: 'my-policy-card',
  pendingCount: 'my-policies-pending-count',
  empty: 'my-policies-empty',
  policiesTab: 'my-policies-tab-policies',
  historyTab: 'my-policies-tab-history',
  historyTable: 'my-policies-history-table',
} as const;

export const acknowledgmentsPageIds = {
  overallCompliance: 'acknowledgments-overall-compliance',
  pendingPolicies: 'acknowledgments-pending-policies',
  totalUsers: 'acknowledgments-total-users',
  totalAcknowledgements: 'acknowledgments-total-acknowledgements',
  perPolicyTab: 'acknowledgments-per-policy-tab',
  perUserTab: 'acknowledgments-per-user-tab',
} as const;

export const policiesTableIds = {
  newPolicyButton: 'policies-new-policy-button',
  statusFilter: 'policies-status-filter',
  table: 'policies-table',
} as const;

export const createPolicyIds = {
  modal: 'create-policy-modal',
  nameInput: 'create-policy-name-input',
  ownerDropdown: 'create-policy-owner-dropdown',
  saveButton: 'create-policy-save-button',
  cancelButton: 'create-policy-cancel-button',
} as const;

export const rejectPolicyIds = {
  modal: 'reject-policy-modal',
  reason: 'reject-policy-reason',
  confirmButton: 'reject-policy-confirm-button',
  cancelButton: 'reject-policy-cancel-button',
} as const;

// AI SYSTEM REGISTRATION DECISION
export const registrationDecisionIds = {
  banner: 'registration-decision-banner',
  approveButton: 'registration-decision-approve-button',
  approveReason: 'registration-decision-approve-reason',
  rejectButton: 'registration-decision-reject-button',
  rejectReason: 'registration-decision-reject-reason',
  resubmitBanner: 'registration-resubmit-banner',
  resubmitButton: 'registration-resubmit-button',
} as const;

// POLICY TEMPLATES (Admin)

export const templatesTableIds = {
  newTemplateButton: 'templates-new-template-button',
  statusFilter: 'templates-status-filter',
  frameworkFilter: 'templates-framework-filter',
  table: 'templates-table',
} as const;

export const createTemplateIds = {
  modal: 'create-template-modal',
  nameInput: 'create-template-name-input',
  descriptionInput: 'create-template-description-input',
  saveButton: 'create-template-save-button',
  cancelButton: 'create-template-cancel-button',
} as const;

export const templateVersionDetailIds = {
  publishButton: 'template-version-publish-button',
  unarchiveButton: 'template-version-unarchive-button',
  moreMenu: 'template-version-more-menu',
  archiveMenuItem: 'template-version-archive-menu-item',
} as const;

export const templateDetailIds = {
  openDraftButton: 'template-detail-open-draft-button',
  unarchiveButton: 'template-detail-unarchive-button',
  moreMenu: 'template-detail-more-menu',
  archiveMenuItem: 'template-detail-archive-menu-item',
} as const;

export const templatePanelIds = {
  nameInput: 'template-name-input',
  contentEditor: 'template-content-editor',
  saveButton: 'template-save-button',
} as const;

export const templateInfoIds = {
  statusBadge: 'template-info-status-badge',
  version: 'template-info-version',
  publishedBy: 'template-info-published-by',
  modifiedAt: 'template-info-modified-at',
  modifiedBy: 'template-info-modified-by',
} as const;

export const templateVersionInfoIds = {
  statusBadge: 'template-version-status-badge',
  publishedBy: 'template-version-published-by',
  modifiedAt: 'template-version-modified-at',
  modifiedBy: 'template-version-modified-by',
} as const;

export const templatesCatalogIds = {
  card: 'templates-catalog-card',
  searchInput: 'templates-catalog-search-input',
  frameworkFilter: 'templates-catalog-framework-filter',
  previewButton: 'templates-catalog-preview-button',
  createPolicyButton: 'templates-catalog-create-policy-button',
} as const;

export const templatePreviewIds = {
  createPolicyButton: 'template-preview-create-policy-button',
  closeButton: 'template-preview-close-button',
} as const;

export const aiSystemWizardIds: { [key: string]: string } = {
  nameInput: 'ai-system-wizard-name-input',
  intendedUseInput: 'ai-system-wizard-intended-use-input',
  descriptionInput: 'ai-system-wizard-description-input',
  currentStateSelect: 'ai-system-wizard-current-state-select',
  ownerSelect: 'ai-system-wizard-owner-select',
  geographicScope: 'ai-system-wizard-geographic-scope',
  runScope: 'ai-system-wizard-run-scope',
  euApplicability: 'ai-system-wizard-eu-applicability',
  addSourceButton: 'ai-system-wizard-add-source-button',
  addEvidenceButton: 'ai-system-wizard-add-evidence-button',
  resetFrameworksButton: 'ai-system-wizard-reset-frameworks-button',
  backButton: 'ai-system-wizard-back-button',
  moreMenu: 'ai-system-wizard-more-menu',
  deleteAction: 'ai-system-wizard-delete-action',
  continueButton: 'ai-system-wizard-continue-button',
  quotaBanner: 'ai-system-wizard-quota-banner',
  quotaBannerUsageLink: 'ai-system-wizard-quota-banner-usage-link',
  viewUsageButton: 'ai-system-wizard-view-usage-button',
};

export const mySubmissionsIds: { [key: string]: string } = {
  moreMenu: 'my-submissions-more-menu',
  deleteAction: 'my-submissions-delete-action',
};

export const usageIds = {
  quotaBlockedBanner: 'usage-quota-blocked-banner',
};
